import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { t } = useLang();
  const { login } = useAuth();
  const nav = useNavigate();
  const [alias, setAlias] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(alias.trim(), password);
      toast.success(t("welcome"));
      nav("/discover");
    } catch (err) {
      toast.error(t("invalidCreds"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12" data-testid="login-page">
      <div className="w-full max-w-md fade-up">
        <div className="bg-white border border-[hsl(var(--border))] rounded-3xl p-8 shadow-[0_4px_24px_rgba(31,30,27,0.04)]">
          <h1 className="text-3xl font-heading font-semibold tracking-tight">{t("welcome")}</h1>
          <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">{t("login")}</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label className="text-sm">{t("alias")}</Label>
              <Input
                value={alias}
                onChange={(e) => setAlias(e.target.value)}
                required
                autoComplete="username"
                className="mt-1.5 rounded-xl h-11"
                data-testid="login-alias-input"
              />
            </div>
            <div>
              <Label className="text-sm">{t("password")}</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="mt-1.5 rounded-xl h-11"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-11 rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-white font-medium hover:-translate-y-0.5 transition-all"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("login")}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-[hsl(var(--muted-foreground))]">
            {t("noAccount")}{" "}
            <Link to="/register" className="text-[hsl(var(--primary))] font-medium hover:underline" data-testid="go-register-link">
              {t("register")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
